(ns nosco.ui.dropdowns2
  (:require
   ["react" :as React]
   [hx.hooks :as hooks :refer [useMemo useState]]
   [hx.react :refer [defnc]]
   [nosco.i18n.translate :as t]
   [nosco.react :as nosco-react]
   [nosco.ui.menus :as menus]
   [nosco.ui.modals3 :as modals3]
   [nosco.ui.popup :as popup]
   [nosco.ui.tooltips :as tooltips]
   [nosco.util :as util]))

(declare DropdownMenu)

(defnc Dropdown [{:keys [button button-ref button-style popper-options children open-state on-button-click tooltip]
                  :or {popper-options {:placement "bottom-start"}}}]
  (let [[open? ->open?] (if open-state open-state (useState false))
        [button-ref-cb popper] (popup/use-popper popper-options)
        button-ref (or button-ref (React/useRef))
        mobile-modal-ref (React/useRef)
        ref (React/useRef)
        mobile (util/use-is-mobile?)
        needs-overlay (or (not mobile)
                          (not (React/isValidElement children))
                          (not (identical? (.-type children) DropdownMenu)))
        close-and-return-focus! (fn [] (->open? false) (.focus (.-current button-ref)))
        children
        (useMemo
         (fn []
           (cond
             (fn? children)
             (children {:close close-and-return-focus!})

             (React/isValidElement children)
             (React/cloneElement
              children
              #js {:on-close close-and-return-focus!
                   :mobile-modal-ref mobile-modal-ref})))
         [children])
        ;; Tooltip support - only show when dropdown is closed
        [tooltip-props tooltip-trigger-ref] (tooltips/use-tooltip)
        merged-button-ref (if tooltip
                            (nosco-react/merge-refs button-ref button-ref-cb tooltip-trigger-ref)
                            (nosco-react/merge-refs button-ref button-ref-cb))]
    (popup/use-click-outside open? #(->open? false) ref button-ref mobile-modal-ref)
    (popup/use-escape open? close-and-return-focus!)
    (popup/use-tab open? close-and-return-focus!)
    [:<>
     (React/cloneElement
      button
      #js {:ref merged-button-ref
           :on-click (fn [e]
                       (when on-button-click (on-button-click e))
                       (->open? not))
           :style (cond-> (or button-style {})
                    open? (assoc :visibility "visible"))
           :role "menubutton"
           :aria-haspopup true
           :aria-expanded open?})
     ;; Only show tooltip when dropdown is closed
     (when (and tooltip (not open?))
       [tooltips/Tooltip (assoc tooltip-props :label tooltip)])
     (if needs-overlay
       [popup/Overlay
        {:ref ref
         :open open?
         :popper popper
         :lock-focus false}
        children]
       (when open?
         children))]))

(defnc DropdownMenu
  "A responsive menu wrapping `nosco.ui.menus/Menu`.

  On desktop, this renders as a standard dropdown menu. While on
  mobile, it renders as a sliding panel modal using the same menu
  items across both.

  ## Props

  All props are passed through to `nosco.ui.menus/Menu`.

  ### `:children`

  Like `Dropdown`, the children may be a function which will be called
  with a `:close` key to close the menu.

  ## Usage

  Should be passed as the only child to `Dropdown`:

  ```
  [Dropdown {:button ,,,}
   [DropdownMenu
    (fn [{:keys [close]}]
      [menus/MenuItems
       [menus/MenuSection
        [menus/MenuItem ,,,]]])]]
  ```"
  [{:keys [children
           mobile-modal-title]
    :as props
    :or {mobile-modal-title (t/t "More actions")}}]
  (let [{:keys [on-close mobile-modal-ref]} props
        mobile (util/use-is-mobile?)
        [mobile-open ->mobile-open] (hooks/useState true)
        children (if (fn? children)
                   (children {:close on-close})
                   children)]
    [menus/Menu
     (-> props
         (dissoc :on-close :mobile-modal-ref)
         (nosco-react/merge-props {:class "nosco-dropdown-menu"}))
     (cond
       mobile
       [modals3/MobileModal
        {:ref mobile-modal-ref
         :open mobile-open
         :on-close #(->mobile-open false)
         :on-transition-destroyed on-close}
        [modals3/MobileModalHeader {:on-close #(->mobile-open false)}
         [modals3/MobileModalHeaderTitle {:title mobile-modal-title}]]
        children]

       :else
       children)]))
